import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ActivityComposer, ActivityRow } from '../components/ActivityKit';
import { EmptyState, ErrorNote, Loading, inputClass } from '../components/ui';
import { errorMessage } from '../lib/api';
import { activityTypes as activityTypesApi, listActivities } from '../lib/resources';

type Situacao = 'abertas' | 'concluidas' | 'todas';

/** Início e fim do dia local, em ISO — o backend filtra por `startDate`. */
const dayRange = (days: number): { from: string; to: string } => {
  const from = new Date();
  from.setHours(0, 0, 0, 0);

  const to = new Date(from);
  to.setDate(to.getDate() + days);
  to.setHours(23, 59, 59, 999);

  return { from: from.toISOString(), to: to.toISOString() };
};

const PERIODOS: Array<[string, string]> = [
  ['hoje', 'Hoje'],
  ['semana', 'Próximos 7 dias'],
  ['atrasadas', 'Atrasadas'],
  ['todas', 'Todas'],
];

export function ActivitiesPage() {
  const [situacao, setSituacao] = useState<Situacao>('abertas');
  const [periodo, setPeriodo] = useState('semana');
  const [typeId, setTypeId] = useState('');
  const [search, setSearch] = useState('');

  const typesQuery = useQuery({
    queryKey: ['activity-types'],
    queryFn: () => activityTypesApi.list(),
  });

  const filters = () => {
    const base: Record<string, string | boolean | number> = { take: 100 };

    if (situacao !== 'todas') base.isCompleted = situacao === 'concluidas';
    if (typeId) base.activityTypeId = typeId;
    if (search) base.search = search;

    if (periodo === 'hoje') {
      const { from, to } = dayRange(0);
      base.startDateFrom = from;
      base.startDateTo = to;
    } else if (periodo === 'semana') {
      const { from, to } = dayRange(7);
      base.startDateFrom = from;
      base.startDateTo = to;
    } else if (periodo === 'atrasadas') {
      base.startDateTo = new Date().toISOString();
      base.isCompleted = false;
    }

    return base;
  };

  const activitiesQuery = useQuery({
    queryKey: ['activities', { situacao, periodo, typeId, search }],
    queryFn: () => listActivities(filters()),
  });

  const activities = activitiesQuery.data?.data ?? [];

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900">Atividades</h1>
        <p className="text-sm text-slate-500">
          {activitiesQuery.data?.count ?? 0} atividade(s) no filtro atual
        </p>
      </header>

      <section className="mb-6 rounded-xl border border-slate-200 bg-white p-4">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Nova atividade
        </h2>
        <ActivityComposer />
      </section>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="font-medium text-slate-700">Situação</span>
          <select
            className={`${inputClass} w-40`}
            value={situacao}
            onChange={(event) => setSituacao(event.target.value as Situacao)}
          >
            <option value="abertas">Abertas</option>
            <option value="concluidas">Concluídas</option>
            <option value="todas">Todas</option>
          </select>
        </label>

        <label className="text-sm">
          <span className="font-medium text-slate-700">Período</span>
          <select
            className={`${inputClass} w-44`}
            value={periodo}
            onChange={(event) => setPeriodo(event.target.value)}
          >
            {PERIODOS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm">
          <span className="font-medium text-slate-700">Tipo</span>
          <select
            className={`${inputClass} w-40`}
            value={typeId}
            onChange={(event) => setTypeId(event.target.value)}
          >
            <option value="">Todos</option>
            {(typesQuery.data?.data ?? []).map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm">
          <span className="font-medium text-slate-700">Buscar</span>
          <input
            className={`${inputClass} w-56`}
            placeholder="Título da atividade"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
      </div>

      {activitiesQuery.isLoading && <Loading />}

      {activitiesQuery.isError && (
        <ErrorNote>
          {errorMessage(activitiesQuery.error, 'Falha ao carregar atividades')}
        </ErrorNote>
      )}

      {activitiesQuery.isSuccess && activities.length === 0 && (
        <EmptyState
          title="Nenhuma atividade neste filtro"
          hint="Agende uma acima ou troque o período."
        />
      )}

      {activities.length > 0 && (
        <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
          {activities.map((activity) => (
            <ActivityRow key={activity.id} activity={activity} />
          ))}
        </div>
      )}
    </div>
  );
}
